<script lang="ts">
	import type { CartState } from '../../handlers/cart_state.svelte';
	import type { Scalars } from '$lib/tools/graphdb';

	import InputLabelNumber from '$lib/utils/components/Forms/InputLabelNumber.svelte';
	import TimeWindows from '$lib/utils/components/TimeWindows/TimeWindows.svelte';
	import CalendarInput from '$lib/utils/components/Dates/CalendarInput.svelte';
	import * as Accordion from '$lib/components/ui/accordion';
	import Badge from '$lib/components/ui/badge/badge.svelte';
	import { Checkbox } from '$lib/components/ui/checkbox';
	import { Label } from '$lib/components/ui/label';
	import * as Card from '$lib/components/ui/card';
	import { Loc } from '$lib/tools/locale.svelte';

	let { cart_state }: { cart_state: CartState } = $props();

	function toggle_payment(method: Scalars['payment_method'], checked: boolean) {
		if (checked) {
			if (!cart_state.variables.allowed_payment_methods.includes(method))
				cart_state.variables.allowed_payment_methods.push(method);
		} else {
			cart_state.variables.allowed_payment_methods = cart_state.variables.allowed_payment_methods.filter(
				(m) => m !== method
			);
		}
	}
</script>

<Accordion.Root type="single" class="w-full shadow-sm border-border/60 rounded-lg">
	<Accordion.Item value="settings" class="border rounded-lg bg-card px-6">
		<Accordion.Trigger class="text-sm font-medium hover:no-underline py-2">
			<Card.Header class="px-0 w-full">
				<div class="flex w-full items-center justify-between">
					<Card.Title class="flex items-center gap-3 text-md">
						<Badge variant="default" class="w-6 h-6 rounded-full p-0 flex items-center justify-center text-[11px]"
							>3</Badge
						>
						{Loc('other_settings')}
					</Card.Title>
					<Badge variant="outline" class="font-normal text-muted-foreground">{Loc('optional')}</Badge>
				</div>
			</Card.Header>
		</Accordion.Trigger>
		<Accordion.Content class="pt-2 pb-6">
			<div class="flex flex-col w-full gap-6">
				<div class="space-y-3">
					<Label class="text-xs uppercase font-bold text-muted-foreground">{Loc('payment_methods')}</Label>
					<div class="grid grid-cols-2 gap-4">
						<div class="flex items-center space-x-2 border p-3 rounded-md">
							<Checkbox
								id="cod"
								checked={cart_state.variables.allowed_payment_methods.includes('cash_on_delivery')}
								onCheckedChange={(v) => toggle_payment('cash_on_delivery', v as boolean)}
							/>
							<Label for="cod" class="text-sm font-normal cursor-pointer">{Loc('cash_on_delivery')}</Label>
						</div>
						<div class="flex items-center space-x-2 border p-3 rounded-md">
							<Checkbox
								id="prepaid"
								checked={cart_state.variables.allowed_payment_methods.includes('paid_in_advance')}
								onCheckedChange={(v) => toggle_payment('paid_in_advance', v as boolean)}
							/>
							<Label for="prepaid" class="text-sm font-normal cursor-pointer">{Loc('paid_in_advance')}</Label>
						</div>
					</div>
				</div>

				<div class="flex gap-6 items-center">
					<Label class="text-xs uppercase font-bold text-muted-foreground">{Loc('disable_buyer_notification')}:</Label>
					<div class="border rounded-md p-0 bg-background">
						<Checkbox
							id="no_new_shipment_notifications"
							bind:checked={
								() => cart_state.variables.is_disable_buyer_notification,
								(v) => (cart_state.variables.is_disable_buyer_notification = v)
							}
						/>
					</div>
				</div>

				<div class="flex gap-6 items-center">
					<Label class="text-xs uppercase font-bold text-muted-foreground">{Loc('seller_free_shipping')}:</Label>
					<div class="border rounded-md p-0 bg-background">
						<Checkbox
							id="no_new_shipment_notifications"
							bind:checked={
								() => cart_state.variables.is_seller_free_shipping,
								(v) => (cart_state.variables.is_seller_free_shipping = v)
							}
						/>
					</div>
				</div>

				{#if cart_state.variables.user_role === 'operator'}
					<div class="flex gap-6 items-center">
						<InputLabelNumber
							is_currency
							title="company_discount"
							bind:value={
								() => cart_state.variables.company_discount, (v) => (cart_state.variables.company_discount = v)
							}
						/>
					</div>

					<div class="flex gap-6 items-center">
						<Label class="text-xs uppercase font-bold text-muted-foreground">{Loc('company_free_shipping')}:</Label>
						<div class="border rounded-md p-0 bg-background">
							<Checkbox
								id="no_new_shipment_notifications"
								bind:checked={
									() => cart_state.variables.is_company_free_shipping,
									(v) => (cart_state.variables.is_company_free_shipping = v)
								}
							/>
						</div>
					</div>
				{/if}

				{#if cart_state.variables.user_role === 'seller'}
					<div class="flex gap-6 items-center">
						<InputLabelNumber
							is_currency
							title="seller_discount"
							bind:value={() => cart_state.variables.seller_discount, (v) => (cart_state.variables.seller_discount = v)}
						/>
					</div>
				{/if}

				<div class="flex gap-6 items-center">
					<Label class="text-xs uppercase font-bold text-muted-foreground">{Loc('ready_time')}:</Label>
					<div class="border rounded-md p-0 bg-background">
						<CalendarInput
							bind:value={() => cart_state.variables.ready_time, (v) => (cart_state.variables.ready_time = v)}
						/>
					</div>
				</div>

				<div class="space-y-3">
					<Label class="text-xs uppercase font-bold text-muted-foreground">{Loc('preferred_delivery_windows')}</Label>
					<div class="border rounded-md p-0 bg-background">
						<TimeWindows
							bind:windows={
								() => cart_state.variables.time_windows_utc, (v) => (cart_state.variables.time_windows_utc = v)
							}
						/>
					</div>
				</div>
			</div>
		</Accordion.Content>
	</Accordion.Item>
</Accordion.Root>
